import CustomModal from '../../../../../components/Modal/CustomModal';
import {CustomTextField} from '../../../../../components/CustomTextField';
import {CleanIcon, PersonChipBlack} from '../../../../../components/buttons/IconButtons';
import ButtonIcon from '../../../../../components/buttons/ButtonIcon';
import AddAccessCodeApp from "../Functionality/AddAccessCodeApp";
import {CustomSelect} from "../../../../../components/CustomSelect";
import CustomDateTimePicker from "../../../../../components/CustomDateTimePicker";
import CustomAddChipSearch from "../../../../../components/CustomAddChipSearch";
import React from "react";
import {USER_TYPE_MODEL} from "../../../../../enums/userTypeEnum";
import {userListToLabelEmailPhoneValue} from "../../../../../Apis/functions";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * AddAccessCodeModal renders a modal form to create a new Access Code.
 * It integrates the AddAccessCodeApp hook to manage form state, validation,
 * and submission. It includes fields for assigning users, selecting a cafeteria,
 * choosing start and end times, and providing a description.
 *
 * @param {Object} props - Component props.
 * @param {function} props.onHide - Callback function to close the modal.
 * @param {boolean} props.show - Flag to display the modal.
 * @param {function} props.setModified - Function to update parent state on form submission.
 * @returns {JSX.Element} - Modal component with access code form fields.
 *
 */
const AddAccessCodeModal = (props) => {
    const {onHide, show, setModified } = props;

    const {
        t,
        disabled,
        emptyFields,
        item,
        isValidTime,
        handleChange,
        handleSubmit,
        handleClearFields,
        selectedList,
        setSelectedList,
        cafeteriasOptions
    } = AddAccessCodeApp({ show, onHide, setModified });

    return (
        <CustomModal
            {...props}
            title={t('create_access_code')}
            onConfirm={handleSubmit}
        >
            <div className='row'>
                <div className='col-12 d-flex justify-content-end mb-3'>
                    {/* Button to clear all form fields */}
                    <ButtonIcon
                        className='btn-clean-secondary-600'
                        id='btn_aac_clean'
                        Icon={CleanIcon}
                        title={'clear_instances'}
                        iconProps='btn-icon-svg-12px icon-color-general-00'
                        onClick={handleClearFields}
                    />
                </div>

                {/* Assign To field with autocomplete */}
                <div className={`col-12`}>
                    <CustomAddChipSearch
                        id="slt_aac_assign_to"
                        selectedList={selectedList}
                        setSelectedList={setSelectedList}
                        userTypeIdsSearch={[
                            USER_TYPE_MODEL.VISITOR,
                            USER_TYPE_MODEL.EMPLOYEE,
                            USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE,
                            USER_TYPE_MODEL.SUPPLIER_IN_CHARGE
                        ]}
                        disabled={false}
                        label="assign_to"
                        emptyFields={emptyFields}
                        required={true}
                        maxLength={1}
                        reset={true}
                        keyId={"userId"}
                        showSubSection={true}
                        sanitizeDataList={userListToLabelEmailPhoneValue}
                        Icon={PersonChipBlack}
                    />
                </div>

                {/* Cafeteria select field */}
                <div className={"col-12"}>
                    <CustomSelect
                        disabled={false}
                        id={"slt_aac_cafeteria"}
                        list={cafeteriasOptions || []}
                        name={"foodieCafeteriaId"}
                        onChange={handleChange}
                        emptyFields={emptyFields}
                        required={true}
                        trans={"select_cafeteria"}
                        value={item.foodieCafeteriaId || ''}
                        showNoSelectionOption={false}
                        keyName={"id"}
                    />
                </div>

                {/* From DateTime Picker */}
                <div className={"col-sm-12 col-md-6 col-xxl-6 col-lg-6"}>
                    <CustomDateTimePicker
                        id={'dp_aac_from_time'}
                        name={'fromTime'}
                        disabled={disabled}
                        onChange={(e) => handleChange({ target: { value: e, name: 'fromTime' } })}
                        trans={'start_date_at_time'}
                        value={item?.fromTime}
                        required={true}
                        disablePast={true}
                        openTo={'day'}
                        emptyFields={emptyFields}
                        minDate={new Date()}
                    />
                </div>

                {/* To DateTime Picker */}
                <div className={"col-sm-12 col-md-6 col-xxl-6 col-lg-6"}>
                    <CustomDateTimePicker
                        id={'dp_aac_end_date'}
                        name={'endDate'}
                        disabled={disabled}
                        onChange={(e) => handleChange({ target: { value: e, name: 'toTime' } })}
                        trans={'end_date_at_time'}
                        value={item?.toTime}
                        required={true}
                        disablePast={true}
                        openTo={'day'}
                        emptyFields={emptyFields}
                        isValid={isValidTime}
                        isValidText={"end_date_cannot_be_before_start_date"}
                    />
                </div>

                {/* Description field */}
                <div className='col-12'>
                    <CustomTextField
                        id={"txt_aac_description"}
                        length={512}
                        name={"description"}
                        value={item.description}
                        trans={"description"}
                        onChange={handleChange}
                        required={true}
                        emptyFields={emptyFields}
                        multiline={true}
                        rows={4}
                    />
                </div>
            </div>
        </CustomModal>
    );
};

export default AddAccessCodeModal;
